import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import BudgetProgress from "@/components/BudgetProgress";
import Logo from "@/components/Logo";
import { publicOrg } from "@/lib/api";
import { DONOR } from "@/constants/testIds";

export default function Donor() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { publicOrg(slug).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Not found")); }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="serif text-3xl text-primary">Organization not found</div>
          <Link to="/" className="mt-4 inline-block text-sm text-muted-foreground under-link">Back to TrustDime →</Link>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const totalBudget = data.programs.reduce((s, p) => s + p.budget, 0);
  const totalSpent = data.programs.reduce((s, p) => s + p.spent, 0);

  return (
    <div data-testid={DONOR.page} className="bg-background min-h-screen">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data.logo_data_url ? (
              <img src={data.logo_data_url} alt={data.name} className="h-10 w-10 rounded-md object-cover border border-border" />
            ) : (
              <Link to="/" className="flex items-center text-muted-foreground hover:text-foreground">
                <Logo height={22} />
              </Link>
            )}
            <div className="font-medium text-sm">{data.name}</div>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Public trust page</div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Stewardship report</div>
        {data.trust?.headline ? (
          <h1 className="serif text-5xl md:text-6xl mt-3 text-primary leading-tight">{data.trust.headline}</h1>
        ) : (
          <h1 className="serif text-5xl md:text-6xl mt-3 text-primary leading-tight">{data.name}</h1>
        )}
        {data.mission && <p className="serif italic text-xl text-muted-foreground mt-6 max-w-3xl">&ldquo;{data.mission}&rdquo;</p>}
        {data.trust?.mission_long && <p className="mt-8 text-base text-foreground/80 max-w-3xl leading-relaxed whitespace-pre-wrap">{data.trust.mission_long}</p>}
        {data.public_description && !data.trust?.mission_long && <p className="mt-8 text-base text-foreground/80 max-w-3xl leading-relaxed">{data.public_description}</p>}

        <div className="mt-14 grid sm:grid-cols-3 gap-6">
          <Big label="Programs" value={data.programs.length} />
          <Big label="Total committed" value={`$${totalBudget.toLocaleString()}`} />
          <Big label="Total deployed" value={`$${totalSpent.toLocaleString()}`} sub={totalBudget ? `${((totalSpent / totalBudget) * 100).toFixed(0)}% of budget` : "—"} />
        </div>
        {data.donations_summary && (
          <div className="mt-6 rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-5 py-4 text-sm">
            <strong>${data.donations_summary.total_usd.toLocaleString()}</strong> in donations from <strong>{data.donations_summary.count_12mo}</strong> gifts over the last 12 months.
          </div>
        )}
      </section>

      {data.campaigns?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-16">
          <h2 className="serif text-3xl text-primary mb-6">Active campaigns</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.campaigns.map((c) => {
              const pct = c.goal_amount ? Math.min(100, Math.round((c.raised_amount / c.goal_amount) * 100)) : 0;
              return (
                <div key={c.name} className="rounded-md border border-border bg-card p-5">
                  <div className="font-medium">{c.name}</div>
                  <div className="mt-3 flex items-baseline justify-between text-sm">
                    <span className="serif text-xl text-primary">${c.raised_amount.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground">of ${c.goal_amount.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-accent rounded-full mt-2"><div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} /></div>
                  <div className="text-[11px] text-muted-foreground mt-1.5">{c.donor_count} donor{c.donor_count === 1 ? "" : "s"} · {pct}%</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {data.programs?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
          <h2 className="serif text-3xl text-primary mb-8">Programs</h2>
          <div className="space-y-4">
            {data.programs.map((p) => (
              <div key={p.name} data-testid={DONOR.programItem(p.name.toLowerCase().replace(/\s+/g, "-"))} className="border border-border bg-card rounded-md p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="serif text-xl text-primary">{p.name}</div>
                    {p.description && <p className="text-sm text-muted-foreground mt-1.5">{p.description}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Budget</div>
                    <div className="mono text-base text-primary tabular">${p.budget.toLocaleString()}</div>
                  </div>
                </div>
                <div className="mt-5">
                  <BudgetProgress value={p.spent} max={p.budget} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.trust?.impact_stories?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
          <h2 className="serif text-3xl text-primary mb-8">Impact stories</h2>
          <div className="space-y-6">
            {data.trust.impact_stories.map((s, i) => (
              <article key={i} className="rounded-md border border-border bg-card p-6">
                {s.title && <h3 className="font-medium">{s.title}</h3>}
                {s.body && <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{s.body}</p>}
              </article>
            ))}
          </div>
        </section>
      )}

      {(data.trust?.annual_reports?.length > 0 || data.trust?.transparency_docs?.length > 0) && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
          <h2 className="serif text-3xl text-primary mb-6">Transparency</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            {[...(data.trust.annual_reports || []), ...(data.trust.transparency_docs || [])].map((d, i) => (
              <a key={i} href={d.url} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-md border border-border bg-card px-4 py-3 hover:bg-accent under-link">
                <span>{d.title || d.year || "Document"}</span>
                <ExternalLink size={14} />
              </a>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-border py-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <Logo height={22} />
            <span>Powered By <a href="https://www.itprofound.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">ITProFound</a> · Stewardship as a service</span>
          </div>
          <a href="/" className="flex items-center gap-1 under-link">Learn more <ExternalLink size={12} /></a>
        </div>
      </footer>
    </div>
  );
}

function Big({ label, value, sub }) {
  return (
    <div className="border border-border bg-card rounded-md p-6">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="serif text-3xl mt-2 text-primary tabular mono">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
